import { Injectable, NgZone } from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { GUID_EMPTY } from '@core';
import { _HttpClient } from '@delon/theme';
import { lastValueFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

@Injectable({ providedIn: 'root' })
export class StockBundleHelperService {
  private headerQtyMap = new WeakMap<FormGroup, number>();
  private headerUomRateMap = new WeakMap<FormGroup, number>();

  constructor(private http: _HttpClient, private zone: NgZone) {}

  isBundleStock(stock: any): boolean {
    return stock?.isBundled === true;
  }

  async expandBundleSelection(options: {
    formArray: FormArray;
    headerLine: FormGroup;
    headerIndex: number;
    stock: any;
    purchase: boolean;
    currencyRate?: number | null;
    customerId?: any;
    supplierId?: any;
    transactionDate?: string | null;
    createLine: () => FormGroup;
    setupLine?: (line: FormGroup) => void;
    recalcLine?: (line: FormGroup) => void;
  }): Promise<boolean> {
    const { formArray, headerLine, headerIndex, stock, purchase } = options;

    if (!this.isBundleStock(stock)) return false;

    const headerId = this.ensureHeaderLine(headerLine);
    // Keep bundle header tax-inclusive mode aligned with document-level mode
    // (covers purchase flows that don't patch line.isTaxInclusive before expansion).
    this.syncHeaderTaxInclusiveFromDocument(formArray, headerLine);
    this.syncHeaderProjectAndLocationFromDocument(formArray, headerLine);
    this.headerQtyMap.set(headerLine, this.getDecimal(headerLine, 'qty'));
    this.headerUomRateMap.set(headerLine, this.getLineUomRate(headerLine));

    // Bundle header should not carry tax code (sub-items handle tax)
    this.clearTaxCode(headerLine);
    // Bundle header should not keep GL account from previous non-bundle selection.
    this.clearAccount(headerLine);
    await this.applyDefaultValuesToBundleHeader(headerLine, stock, purchase, options);
    await this.hydrateBundleHeaderStock(headerLine, stock);
    this.headerUomRateMap.set(headerLine, this.getLineUomRate(headerLine));

    const bundleItems = await lastValueFrom(
      this.http.get(`api/StockBundles/${stock.id}`)
    );

    // Important: run inserts inside Angular zone so the table updates immediately
    // without requiring a scroll/interaction to trigger change detection.
    this.zone.run(() => {
      let insertIndex = headerIndex + 1;
      for (const bundleItem of bundleItems || []) {
        const line = options.createLine();
        this.ensureSubItemLine(line, headerId, bundleItem, purchase, headerLine);
        formArray.insert(insertIndex, line);
        options.setupLine?.(line);
        options.recalcLine?.(line);

        // Populate tax codes for sub-items based on stock maintenance/default values.
        // Do this async (non-blocking) so callers can re-enable observeChanges quickly.
        this.applyDefaultTaxCodeToLine(line, purchase, options).then(() => {
          this.zone.run(() => {
            // Recalc after tax code is applied (tax amounts depend on it)
            options.recalcLine?.(line);
            this.updateBundleHeaderFromSubItems(formArray, headerLine);
          });
        });
        insertIndex++;
      }

      this.updateBundleHeaderFromSubItems(formArray, headerLine);
      // Nudge reactive forms + table bindings
      try {
        formArray.updateValueAndValidity({ emitEvent: false });
        headerLine.updateValueAndValidity({ emitEvent: false });
      } catch {}
    });
    return true;
  }

  private syncHeaderTaxInclusiveFromDocument(
    formArray: FormArray,
    headerLine: FormGroup
  ): void {
    try {
      const docTaxInclusiveCtrl = formArray.parent?.get('isTaxInclusive');
      const headerTaxInclusiveCtrl = headerLine.get('isTaxInclusive');
      if (!docTaxInclusiveCtrl || !headerTaxInclusiveCtrl) return;
      headerTaxInclusiveCtrl.setValue(Boolean(docTaxInclusiveCtrl.value), {
        emitEvent: false
      });
    } catch {
      // ignore if transaction form doesn't have tax-inclusive control
    }
  }

  private syncHeaderProjectAndLocationFromDocument(
    formArray: FormArray,
    headerLine: FormGroup
  ): void {
    try {
      const parent = formArray.parent;
      if (!parent) return;

      const docProject = parent.get('project')?.value ?? null;
      const docProjectId = parent.get('projectId')?.value ?? null;
      const docLocation = parent.get('stockLocation')?.value ?? null;
      const docLocationId = parent.get('stockLocationId')?.value ?? null;

      if (headerLine.get('project')) {
        headerLine.get('project')!.setValue(docProject, { emitEvent: false });
      }
      if (headerLine.get('projectId')) {
        headerLine.get('projectId')!.setValue(docProjectId, { emitEvent: false });
      }
      if (headerLine.get('stockLocation')) {
        headerLine.get('stockLocation')!.setValue(docLocation, {
          emitEvent: false
        });
      }
      if (headerLine.get('stockLocationId')) {
        headerLine.get('stockLocationId')!.setValue(docLocationId, {
          emitEvent: false
        });
      }
    } catch {
      // ignore if transaction doesn't have project/location controls
    }
  }

  private async applyDefaultTaxCodeToLine(
    line: FormGroup,
    purchase: boolean,
    options: {
      currencyRate?: number | null;
      customerId?: any;
      supplierId?: any;
      transactionDate?: string | null;
    }
  ): Promise<void> {
    const stockId =
      Number(line.get('stockId')?.value ?? line.get('stock')?.value?.id ?? 0) ||
      0;
    if (!stockId) return;

    // If this transaction doesn't even have tax controls, don't call DefaultValues here.
    // (E.g. many Stock transactions have no tax columns.)
    if (!line.get('taxCode') && !line.get('taxCodeId') && !line.get('taxRate')) {
      return;
    }

    // If line already has a tax code set, respect it.
    const existing = line.get('taxCode')?.value;
    if (existing) return;

    const qty = Number(line.get('qty')?.value ?? 1) || 1;

    const params: any = {
      stockId,
      purchase,
      qty
    };
    if (options.currencyRate != null) params.currencyRate = options.currencyRate;
    if (!purchase && options.customerId != null) params.customerId = options.customerId;
    if (purchase && options.supplierId != null) params.supplierId = options.supplierId;
    if (options.transactionDate != null) params.transactionDate = options.transactionDate;

    try {
      const res: any = await lastValueFrom(
        this.http.get('api/stocks/DefaultValues', params)
      );
      const taxCode = purchase ? res?.inputTaxCode : res?.outputTaxCode;
      const taxId = taxCode?.id ?? null;
      const taxRate = Number(taxCode?.rate ?? 0) || 0;
      const exemptionRate = Number(taxCode?.exemptionRate ?? 0) || 0;

      // Initialize line GL account when transaction line supports it.
      // Cash Sales requires cashSalesAccount, while other sales/purchase flows
      // use salesAccount/purchaseAccount.
      const glAccount = purchase
        ? (res?.cashPurchaseAccount ?? res?.purchaseAccount ?? null)
        : (res?.cashSalesAccount ?? res?.salesAccount ?? null);

      if (line.get('account'))
        line.get('account')!.setValue(glAccount, { emitEvent: false });
      if (line.get('accountId'))
        line.get('accountId')!.setValue(glAccount?.id ?? null, {
          emitEvent: false
        });

      if (line.get('taxCode'))
        line.get('taxCode')!.setValue(taxCode ?? null, { emitEvent: false });
      if (line.get('taxCodeId'))
        line.get('taxCodeId')!.setValue(taxId, { emitEvent: false });
      if (line.get('taxRate'))
        line.get('taxRate')!.setValue(taxRate, { emitEvent: false });
      if (line.get('taxExemptionRate'))
        line
          .get('taxExemptionRate')!
          .setValue(exemptionRate, { emitEvent: false });
    } catch {
      // ignore; allow user to input tax code manually
    }
  }

  private clearTaxCode(line: FormGroup): void {
    try {
      if (line.get('taxCode')) line.get('taxCode')!.setValue(null);
      if (line.get('taxCodeId')) line.get('taxCodeId')!.setValue(null);
      if (line.get('taxRate')) line.get('taxRate')!.setValue(0);
      if (line.get('taxExemptionRate')) line.get('taxExemptionRate')!.setValue(0);
    } catch {}
  }

  private clearAccount(line: FormGroup): void {
    try {
      if (line.get('account')) line.get('account')!.setValue(null);
      if (line.get('accountId')) line.get('accountId')!.setValue(null);
    } catch {}
  }

  syncBundleSubItemQty(formArray: FormArray, headerLine: FormGroup): void {
    const headerQty = this.getDecimal(headerLine, 'qty');
    const lastQty = this.headerQtyMap.get(headerLine) ?? headerQty;
    if (lastQty === 0 || headerQty === lastQty) {
      this.headerQtyMap.set(headerLine, headerQty);
      return;
    }

    const ratio = headerQty / lastQty;
    const headerId = this.getString(headerLine, 'id');
    if (!headerId) return;

    formArray.controls.forEach(line => {
      if (this.getString(line, 'bundleId') !== headerId) return;
      if (!this.getBoolean(line, 'isSubItem')) return;

      const qty = this.getDecimal(line, 'qty');
      this.setDecimal(line, 'qty', qty * ratio);
    });

    this.headerQtyMap.set(headerLine, headerQty);
  }

  updateBundleHeaderFromSubItems(formArray: FormArray, headerLine: FormGroup): void {
    const headerId = this.getString(headerLine, 'id');
    if (!headerId) return;

    const sumFields = [
      'amount',
      'amountLocal',
      'discountAmount',
      'discountAmountLocal',
      'subAmount',
      'subAmountLocal',
      'taxAmount',
      'taxAmountLocal',
      'wTaxAmount',
      'wTaxAmountLocal',
      'wVatAmount',
      'wVatAmountLocal',
      'taxExclusiveAmount',
      'taxExclusiveAmountLocal',
      'netAmount',
      'netAmountLocal'
    ];

    const totals: Record<string, number> = {};
    sumFields.forEach(field => (totals[field] = 0));

    formArray.controls.forEach(line => {
      if (this.getString(line, 'bundleId') !== headerId) return;
      if (!this.getBoolean(line, 'isSubItem')) return;

      sumFields.forEach(field => {
        totals[field] += this.getDecimal(line, field);
      });
    });

    sumFields.forEach(field => {
      this.setDecimal(headerLine, field, totals[field]);
    });

    const headerQty = this.getDecimal(headerLine, 'qty');
    if (headerQty !== 0) {
      this.setDecimal(headerLine, 'unitPrice', totals['amount'] / headerQty);
    }
  }

  updateBundleHeaderFromLine(formArray: FormArray, line: FormGroup): void {
    if (this.getBoolean(line, 'isSubItem')) {
      const headerId = this.getString(line, 'bundleId');
      if (!headerId) return;
      const headerLine = formArray.controls.find(
        ctrl => ctrl.get('id')?.value === headerId
      ) as FormGroup | undefined;
      if (headerLine) {
        this.updateBundleHeaderFromSubItems(formArray, headerLine);
      }
      return;
    }

    if (this.getBoolean(line, 'isBundled') && !this.getBoolean(line, 'isSubItem')) {
      this.syncBundleSubItemByHeaderUomRate(formArray, line);
      this.updateBundleHeaderFromSubItems(formArray, line);
    }
  }

  primeHeaderQtyMap(formArray: FormArray): void {
    formArray.controls.forEach(control => {
      const line = control as FormGroup;
      if (
        this.getBoolean(line, 'isBundled') &&
        !this.getBoolean(line, 'isSubItem')
      ) {
        this.headerQtyMap.set(line, this.getDecimal(line, 'qty'));
      }
    });
  }

  ensureBundleIds(formArray: FormArray): void {
    let currentHeaderId: string | null = null;

    formArray.controls.forEach(control => {
      const line = control as FormGroup;
      const isHeader =
        this.getBoolean(line, 'isBundled') &&
        !this.getBoolean(line, 'isSubItem');
      const isSubItem = this.getBoolean(line, 'isSubItem');

      if (isHeader) {
        currentHeaderId = this.ensureHeaderLine(line);
        return;
      }

      if (isSubItem) {
        const bundleId = this.getString(line, 'bundleId');
        if (!bundleId && currentHeaderId) {
          this.setString(line, 'bundleId', currentHeaderId);
        }
        return;
      }

      currentHeaderId = null;
    });
  }

  removeBundleGroup(formArray: FormArray, index: number): boolean {
    const line = formArray.at(index) as FormGroup | null;
    if (!line) return false;

    if (!this.getBoolean(line, 'isBundled') || this.getBoolean(line, 'isSubItem'))
      return false;

    const headerId = this.getString(line, 'id');
    if (!headerId) return false;

    for (let i = formArray.length - 1; i >= 0; i--) {
      const current = formArray.at(i) as FormGroup;
      if (this.getString(current, 'bundleId') === headerId || i === index) {
        formArray.removeAt(i);
      }
    }

    return true;
  }

  prepareLineForStockChange(formArray: FormArray, line: FormGroup): void {
    const currentIndex = formArray.controls.indexOf(line);
    if (currentIndex < 0) return;

    const isHeader =
      this.getBoolean(line, 'isBundled') && !this.getBoolean(line, 'isSubItem');
    if (isHeader) {
      const headerId = this.getString(line, 'id');
      if (headerId) {
        for (let i = formArray.length - 1; i >= 0; i--) {
          if (i === currentIndex) continue;
          const current = formArray.at(i) as FormGroup;
          const linkedToHeader = this.getString(current, 'bundleId') === headerId;
          const isSubItem = this.getBoolean(current, 'isSubItem');
          if (linkedToHeader && isSubItem) {
            formArray.removeAt(i);
          }
        }
      } else {
        // Fallback for copied lines that still don't have normalized ids:
        // remove contiguous sub-items directly under the header row.
        for (let i = formArray.length - 1; i > currentIndex; i--) {
          const current = formArray.at(i) as FormGroup;
          const isSubItem = this.getBoolean(current, 'isSubItem');
          if (!isSubItem) continue;

          // Only remove rows that belong to this visible block.
          // If there's a non-subitem between header and row, stop.
          let blocked = false;
          for (let j = currentIndex + 1; j < i; j++) {
            const between = formArray.at(j) as FormGroup;
            if (!this.getBoolean(between, 'isSubItem')) {
              blocked = true;
              break;
            }
          }
          if (!blocked) {
            formArray.removeAt(i);
          }
        }
      }
    }

    this.setBoolean(line, 'isBundled', false);
    this.setBoolean(line, 'isSubItem', false);
    this.setString(line, 'bundleId', null);
    this.headerQtyMap.delete(line);
    this.headerUomRateMap.delete(line);
  }

  private ensureHeaderLine(line: FormGroup): string {
    const headerId = this.getString(line, 'id') || uuidv4();
    this.setString(line, 'id', headerId);
    this.setBoolean(line, 'isBundled', true);
    this.setBoolean(line, 'isSubItem', false);
    this.setString(line, 'bundleId', null);
    this.headerUomRateMap.set(line, this.getLineUomRate(line));
    return headerId;
  }

  private async applyDefaultValuesToBundleHeader(
    line: FormGroup,
    stock: any,
    purchase: boolean,
    options: {
      currencyRate?: number | null;
      customerId?: any;
      supplierId?: any;
      transactionDate?: string | null;
    }
  ): Promise<void> {
    const stockId = Number(stock?.id ?? line.get('stockId')?.value ?? 0) || 0;
    if (!stockId) return;

    const qty = Number(line.get('qty')?.value ?? 1) || 1;
    const params: any = { stockId, purchase, qty };
    if (options.currencyRate != null) params.currencyRate = options.currencyRate;
    if (!purchase && options.customerId != null) params.customerId = options.customerId;
    if (purchase && options.supplierId != null) params.supplierId = options.supplierId;
    if (options.transactionDate != null) params.transactionDate = options.transactionDate;

    try {
      const res: any = await lastValueFrom(
        this.http.get('api/stocks/DefaultValues', params)
      );
      if (!res) return;

      if (line.get('uom')) line.get('uom')!.setValue(res.uom ?? null, { emitEvent: false });
      if (line.get('uomId'))
        line
          .get('uomId')!
          .setValue(res.uomId ?? res.uom?.id ?? null, { emitEvent: false });
      if (line.get('unitPrice'))
        line.get('unitPrice')!.setValue(Number(res.unitPrice ?? 0), { emitEvent: false });
    } catch {
      // keep current line values if default value lookup fails
    }
  }

  private syncBundleSubItemByHeaderUomRate(
    formArray: FormArray,
    headerLine: FormGroup
  ): void {
    const headerId = this.getString(headerLine, 'id');
    if (!headerId) return;

    const currentRate = this.getLineUomRate(headerLine);
    const previousRate = this.headerUomRateMap.get(headerLine) ?? currentRate;
    if (previousRate === 0 || currentRate === previousRate) {
      this.headerUomRateMap.set(headerLine, currentRate);
      return;
    }

    const ratio = currentRate / previousRate;
    const amountRatio = ratio * ratio;
    const monetaryFields = [
      'amount',
      'amountLocal',
      'discountAmount',
      'discountAmountLocal',
      'subAmount',
      'subAmountLocal',
      'taxAmount',
      'taxAmountLocal',
      'wTaxAmount',
      'wTaxAmountLocal',
      'wVatAmount',
      'wVatAmountLocal',
      'taxExclusiveAmount',
      'taxExclusiveAmountLocal',
      'netAmount',
      'netAmountLocal'
    ];

    formArray.controls.forEach(line => {
      if (this.getString(line, 'bundleId') !== headerId) return;
      if (!this.getBoolean(line, 'isSubItem')) return;

      this.setDecimal(line, 'qty', this.getDecimal(line, 'qty') * ratio);
      this.setDecimal(line, 'unitPrice', this.getDecimal(line, 'unitPrice') * ratio);
      monetaryFields.forEach(field => {
        const ctrl = line.get(field);
        if (!ctrl) return;
        this.setDecimal(line, field, this.getDecimal(line, field) * amountRatio);
      });
    });

    this.headerUomRateMap.set(headerLine, currentRate);
  }

  private ensureSubItemLine(
    line: FormGroup,
    headerId: string,
    bundleItem: any,
    purchase: boolean,
    headerLine: FormGroup
  ): void {
    this.setString(line, 'id', uuidv4());
    this.setBoolean(line, 'isBundled', false);
    this.setBoolean(line, 'isSubItem', true);
    this.setString(line, 'bundleId', headerId);

    const headerQty = this.getDecimal(headerLine, 'qty') || 1;
    const qty = Number(bundleItem?.qty || 0) * headerQty;

    this.setAny(line, 'stock', bundleItem?.subItem ?? null);
    this.setAny(line, 'stockId', bundleItem?.subItemId ?? null);
    this.setAny(line, 'uom', bundleItem?.uom ?? null);
    this.setAny(line, 'uomId', bundleItem?.uomId ?? null);
    this.setString(line, 'description', bundleItem?.description ?? bundleItem?.subItem?.name ?? '');
    this.copyProjectAndLocationFromHeader(line, headerLine);
    // Keep sub-item tax-inclusive mode aligned with bundle header when supported.
    if (line.get('isTaxInclusive') && headerLine.get('isTaxInclusive')) {
      this.setBoolean(
        line,
        'isTaxInclusive',
        Boolean(headerLine.get('isTaxInclusive')?.value)
      );
    }
    this.setDecimal(line, 'qty', qty);

    const price = purchase ? bundleItem?.purchasePrice : bundleItem?.listPrice;
    this.setDecimal(line, 'unitPrice', Number(price || 0));
  }

  private copyProjectAndLocationFromHeader(
    line: FormGroup,
    headerLine: FormGroup
  ): void {
    if (line.get('project') && headerLine.get('project')) {
      line.get('project')!.setValue(headerLine.get('project')!.value ?? null, {
        emitEvent: false
      });
    }
    if (line.get('projectId') && headerLine.get('projectId')) {
      line
        .get('projectId')!
        .setValue(headerLine.get('projectId')!.value ?? null, {
          emitEvent: false
        });
    }

    if (line.get('stockLocation') && headerLine.get('stockLocation')) {
      line
        .get('stockLocation')!
        .setValue(headerLine.get('stockLocation')!.value ?? null, {
          emitEvent: false
        });
    }
    if (line.get('stockLocationId') && headerLine.get('stockLocationId')) {
      line
        .get('stockLocationId')!
        .setValue(headerLine.get('stockLocationId')!.value ?? null, {
          emitEvent: false
        });
    }
  }

  private async hydrateBundleHeaderStock(
    line: FormGroup,
    selectedStock: any
  ): Promise<void> {
    const stockId =
      Number(line.get('stockId')?.value ?? selectedStock?.id ?? 0) || 0;
    const stockControl = line.get('stock');
    if (!stockId || !stockControl) return;

    // Preserve the current selected object reference so stock-select keeps showing
    // the selected option after bundle expansion.
    const currentStock = stockControl.value ?? selectedStock ?? null;
    if (!currentStock || typeof currentStock !== 'object') {
      stockControl.setValue(selectedStock ?? null, { emitEvent: false });
      return;
    }

    const hasMeta = Boolean(
      currentStock?.category || currentStock?.group || currentStock?.class
    );
    if (hasMeta) {
      stockControl.setValue(currentStock, { emitEvent: false });
      return;
    }

    try {
      const fullStock: any = await lastValueFrom(
        this.http.get(`api/stocks/${stockId}`)
      );
      if (fullStock) {
        Object.assign(currentStock, {
          category: fullStock?.category ?? currentStock?.category,
          group: fullStock?.group ?? currentStock?.group,
          class: fullStock?.class ?? currentStock?.class,
          description: fullStock?.description ?? currentStock?.description,
          name: fullStock?.name ?? currentStock?.name,
          code: fullStock?.code ?? currentStock?.code
        });
      }
      stockControl.setValue(currentStock, { emitEvent: false });
    } catch {
      stockControl.setValue(currentStock, { emitEvent: false });
    }
  }

  private getBoolean(line: AbstractControl, key: string): boolean {
    const control = line.get(key);
    return control ? Boolean(control.value) : false;
  }

  private getDecimal(line: AbstractControl, key: string): number {
    const control = line.get(key);
    return control ? Number(control.value || 0) : 0;
  }

  private getString(line: AbstractControl, key: string): string | null {
    const control = line.get(key);
    const value = control ? control.value ?? null : null;
    if (!value) return null;
    return value === GUID_EMPTY ? null : value;
  }

  private getLineUomRate(line: AbstractControl): number {
    const uom = line.get('uom')?.value;
    const rate = Number(uom?.rate ?? 1) || 1;
    return rate > 0 ? rate : 1;
  }

  private setBoolean(line: AbstractControl, key: string, value: boolean): void {
    const control = line.get(key);
    if (control) control.setValue(value);
  }

  private setDecimal(line: AbstractControl, key: string, value: number): void {
    const control = line.get(key);
    if (control) control.setValue(value);
  }

  private setString(line: AbstractControl, key: string, value: string | null): void {
    const control = line.get(key);
    if (control) control.setValue(value);
  }

  private setAny(line: AbstractControl, key: string, value: any): void {
    const control = line.get(key);
    if (control) control.setValue(value);
  }
}
